/**
 * Configurações › Documentos: dados do escritório usados nos documentos,
 * timbrado e modelos (texto editável ou arquivo Word com {{campos}}).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCatalog, useLookups } from "@/lib/catalog";
import { Badge, Card, ErrorBox, Field, FormGrid, Loading, Modal, useAct } from "@/ui/kit";
import { confirmar, toast } from "@/ui/feedback";
import { Paper, type InputDef } from "@/ui/generator";
import { NewWordTemplate, TemplateEditor } from "@/ui/template-editor";

interface Office {
  advogado: string; genero: "F" | "M"; qualificacao: string; oabs: { uf: string; numero: string }[];
  telefone: string; email: string; endereco: string; cidade: string; uf: string; razaoSocial: string; cnpj: string;
  margens: { top: number; bottom: number; left: number; right: number };
}
interface TemplateRow {
  id: string; key: string; name: string; description: string | null; context: "pessoa" | "caso" | "processo" | "financeiro"; kind: "texto" | "docx";
  body: string | null; fileName: string | null; inputs: InputDef[]; required: string[]; letterhead: boolean; letterheadId: string | null; active: boolean; version: number; updatedAt: string;
}

export function DocumentSettings(): JSX.Element {
  return (
    <div className="stack gap-16">
      <OfficeCard />
      <Letterheads />
      <Templates />
    </div>
  );
}

// ------------------------------------------------------------- escritório

function OfficeCard(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-escritorio"], queryFn: () => api.get<Office>("/config/escritorio") });
  const [f, setF] = useState<Office | null>(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  const save = useAct(() => {
    const body = f!;
    return api.put("/config/escritorio", { ...body, oabs: body.oabs.filter((o) => o.uf && o.numero) });
  }, [["config-escritorio"]]);
  if (!f) return <Card title="Dados do escritório nos documentos"><ErrorBox error={error} /><Loading /></Card>;
  const set = (patch: Partial<Office>) => setF({ ...f, ...patch });
  return (
    <Card title="Dados do escritório nos documentos" actions={<button className="btn small primary" disabled={save.isPending} onClick={() => save.mutate()}>Salvar</button>}>
      <ErrorBox error={save.error} />
      <div className="row" style={{ alignItems: "flex-start", gap: 24 }}>
        <div className="grow" style={{ flex: 1, minWidth: 320 }}>
          <FormGrid cols={3}>
            <Field label="Advogada(o) titular" span={2}><input className="input" value={f.advogado} onChange={(e) => set({ advogado: e.target.value })} /></Field>
            <Field label="Gênero (concordância)">
              <select className="input" value={f.genero} onChange={(e) => set({ genero: e.target.value as "F" | "M" })}><option value="F">Feminino</option><option value="M">Masculino</option></select>
            </Field>
            <Field label="Qualificação" span={3} hint="Ex.: brasileira, casada, advogada"><input className="input" value={f.qualificacao} onChange={(e) => set({ qualificacao: e.target.value })} /></Field>
            <Field label="OABs" span={3}>
              <div className="stack gap-4">
                {[...f.oabs, { uf: "", numero: "" }].map((o, i) => (
                  <div key={i} className="row nowrap">
                    <input className="input" style={{ width: 70 }} maxLength={2} placeholder="UF" value={o.uf}
                      onChange={(e) => { const oabs = [...f.oabs]; oabs[i] = { ...o, uf: e.target.value.toUpperCase() }; set({ oabs }); }} />
                    <input className="input" placeholder="Número (58.794)" value={o.numero}
                      onChange={(e) => { const oabs = [...f.oabs]; oabs[i] = { ...o, numero: e.target.value }; set({ oabs }); }} />
                    {i < f.oabs.length && <button className="btn ghost small" onClick={() => set({ oabs: f.oabs.filter((_, j) => j !== i) })}>✕</button>}
                  </div>
                ))}
              </div>
            </Field>
            <Field label="Telefone"><input className="input" value={f.telefone} onChange={(e) => set({ telefone: e.target.value })} /></Field>
            <Field label="E-mail" span={2}><input className="input" value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
            <Field label="Endereço profissional" span={3}><input className="input" value={f.endereco} onChange={(e) => set({ endereco: e.target.value })} /></Field>
            <Field label="Cidade (foro e data)" span={2}><input className="input" value={f.cidade} onChange={(e) => set({ cidade: e.target.value })} /></Field>
            <Field label="UF"><input className="input" maxLength={2} value={f.uf} onChange={(e) => set({ uf: e.target.value.toUpperCase() })} /></Field>
            <Field label="Razão social (recibos)" span={2}><input className="input" value={f.razaoSocial} onChange={(e) => set({ razaoSocial: e.target.value })} /></Field>
            <Field label="CNPJ"><input className="input" value={f.cnpj} onChange={(e) => set({ cnpj: e.target.value })} /></Field>
            <Field label="Margens padrão do papel (mm)" span={3} hint="Cada timbrado pode ter margens próprias; estas valem quando ele não tiver.">
              <div className="row nowrap">
                {(["top", "bottom", "left", "right"] as const).map((k) => (
                  <label key={k} className="row nowrap small">{{ top: "Sup.", bottom: "Inf.", left: "Esq.", right: "Dir." }[k]}
                    <input className="input" type="number" style={{ width: 70 }} value={f.margens[k]}
                      onChange={(e) => set({ margens: { ...f.margens, [k]: Number(e.target.value) } })} />
                  </label>
                ))}
              </div>
            </Field>
          </FormGrid>
        </div>
      </div>
    </Card>
  );
}


// ------------------------------------------------------------ timbrados

interface Margins { top: number; bottom: number; left: number; right: number }
interface LetterheadRow {
  id: string; name: string; isDefault: boolean; active: boolean; width: number; height: number;
  margins: Margins | null; unitIds: string[]; updatedAt: string;
}

function Thumb({ id, v }: { id: string; v: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null;
    void api.blobUrl(`/timbrados/${id}/imagem?v=${encodeURIComponent(v)}`).then((x) => { u = x; setUrl(x); }).catch(() => setUrl(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id, v]);
  return url ? <img className="letterhead-thumb" src={url} alt="" /> : <div className="letterhead-thumb" />;
}

function Letterheads(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-timbrados"], queryFn: () => api.get<LetterheadRow[]>("/config/timbrados") });
  const { unitName } = useLookups();
  const [edit, setEdit] = useState<LetterheadRow | "new" | null>(null);
  const inv = [["config-timbrados"], ["timbrados"]];
  const patch = useAct(({ id, body }: { id: string; body: Record<string, unknown> }) => api.patch(`/config/timbrados/${id}`, body), inv);
  return (
    <Card title="Papéis timbrados" actions={<button className="btn small primary" onClick={() => setEdit("new")}>Enviar timbrado</button>}>
      <ErrorBox error={error ?? patch.error} />
      <div className="muted small" style={{ marginBottom: 10 }}>
        Na hora de gerar vale, nesta ordem: o escolhido pelo usuário → o fixo do modelo → o da unidade do caso/processo → o <strong>padrão</strong>.
        Trocar um timbrado não altera documentos já gerados.
      </div>
      {!data ? <Loading /> : !data.length ? <div className="muted">Nenhum timbrado. Envie o primeiro (PNG, JPEG ou PDF do papel A4).</div> : (
        <div className="lh-grid">
          {data.map((l) => (
            <div key={l.id} className={`lh-card ${l.active ? "" : "off"}`}>
              <Thumb id={l.id} v={l.updatedAt} />
              <div className="stack gap-4" style={{ minWidth: 0 }}>
                <div className="strong">{l.name}</div>
                <div className="row" style={{ gap: 4 }}>
                  {l.isDefault && <Badge tone="ok">padrão</Badge>}
                  {!l.active && <Badge tone="muted">desativado</Badge>}
                  {l.unitIds.map((u) => <Badge key={u} tone="info">{unitName(u)}</Badge>)}
                </div>
                {l.margins && <div className="muted small">margens {l.margins.top}/{l.margins.bottom}/{l.margins.left}/{l.margins.right} mm</div>}
                <div className="row" style={{ gap: 4, marginTop: 4 }}>
                  <button className="btn small" onClick={() => setEdit(l)}>Editar</button>
                  {l.active && !l.isDefault && <button className="btn small ghost" onClick={() => patch.mutate({ id: l.id, body: { isDefault: true } })}>Tornar padrão</button>}
                  <button className="btn small ghost" onClick={() => void (l.active
                    ? confirmar(`Desativar "${l.name}"? Unidades e modelos que usam ele voltam ao padrão.`, { danger: true }).then((ok) => ok && patch.mutate({ id: l.id, body: { active: false } }))
                    : Promise.resolve(patch.mutate({ id: l.id, body: { active: true } })))}>{l.active ? "Desativar" : "Reativar"}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && <LetterheadModal lh={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
    </Card>
  );
}

function LetterheadModal({ lh, onClose }: { lh: LetterheadRow | null; onClose: () => void }): JSX.Element {
  const { data: catalog } = useCatalog();
  const [name, setName] = useState(lh?.name ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [unitIds, setUnitIds] = useState<string[]>(lh?.unitIds ?? []);
  const [ownMargins, setOwnMargins] = useState(!!lh?.margins);
  const [m, setM] = useState<Margins>(lh?.margins ?? { top: 45, bottom: 30, left: 22, right: 22 });
  const [isDefault, setIsDefault] = useState(lh?.isDefault ?? false);
  const inv = [["config-timbrados"], ["timbrados"]];
  const save = useAct(async () => {
    let id = lh?.id;
    if (!id) {
      const fd = new FormData(); fd.append("name", name || file!.name); fd.append("file", file!);
      const r = await api.upload<{ id: string; warning: string | null }>("/config/timbrados", fd);
      if (r.warning) toast.error(r.warning);
      id = r.id;
    } else if (file) {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.upload<{ warning: string | null }>(`/config/timbrados/${id}/arquivo`, fd);
      if (r.warning) toast.error(r.warning);
    }
    await api.patch(`/config/timbrados/${id}`, { name: name || undefined, unitIds, margins: ownMargins ? m : null, ...(isDefault ? { isDefault: true } : {}) });
  }, inv, onClose, lh ? "Timbrado atualizado" : "Timbrado enviado");
  const units = catalog?.units ?? [];
  return (
    <Modal title={lh ? `Timbrado — ${lh.name}` : "Enviar timbrado"} onClose={onClose} footer={
      <><button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={save.isPending || (!lh && !file)} onClick={() => save.mutate()}>{save.isPending ? "Salvando…" : "Salvar"}</button></>
    }>
      <ErrorBox error={save.error} />
      <FormGrid cols={1}>
        <Field label="Nome" hint="Ex.: Timbrado VG, Timbrado BCS, Timbrado sem rodapé"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label={lh ? "Trocar arquivo (opcional)" : "Arquivo"} required={!lh} hint="Página A4 inteira: PNG, JPEG ou PDF (1ª página).">
          <input type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <Field label="Padrão das unidades" hint="Documentos de casos/processos dessas unidades usam este timbrado.">
          <div className="chips">
            {units.map((u) => {
              const on = unitIds.includes(u.id);
              return <button type="button" key={u.id} className={`chip ${on ? "on" : ""}`} onClick={() => setUnitIds(on ? unitIds.filter((x) => x !== u.id) : [...unitIds, u.id])}>{u.name}</button>;
            })}
          </div>
        </Field>
        <label className="row nowrap"><input type="checkbox" checked={isDefault} disabled={lh?.isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Padrão do escritório (quando nada mais se aplica)</label>
        <label className="row nowrap"><input type="checkbox" checked={ownMargins} onChange={(e) => setOwnMargins(e.target.checked)} /> Margens próprias para este papel</label>
        {ownMargins && (
          <div className="row nowrap">
            {(["top", "bottom", "left", "right"] as const).map((k) => (
              <label key={k} className="row nowrap small">{{ top: "Sup.", bottom: "Inf.", left: "Esq.", right: "Dir." }[k]}
                <input className="input" type="number" style={{ width: 70 }} value={m[k]} onChange={(e) => setM({ ...m, [k]: Number(e.target.value) })} /> mm
              </label>
            ))}
          </div>
        )}
      </FormGrid>
    </Modal>
  );
}

// ---------------------------------------------------------------- modelos

const CONTEXT_LABEL = { pessoa: "Pessoa", caso: "Caso", processo: "Processo", financeiro: "Financeiro (recibo)" } as const;

function Templates(): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["config-modelos"], queryFn: () => api.get<TemplateRow[]>("/config/modelos") });
  const [edit, setEdit] = useState<TemplateRow | "new" | null>(null);
  const [editorId, setEditorId] = useState<string | null>(null);
  const [newWord, setNewWord] = useState(false);
  const [legacy, setLegacy] = useState<TemplateRow | null>(null);
  const inv = [["config-modelos"], ["modelos-documento"]];
  const dup = useAct((id: string) => api.post(`/config/modelos/${id}/duplicar`), inv, undefined, "Modelo duplicado");
  const toggle = useAct((t: TemplateRow) => api.patch(`/config/modelos/${t.id}`, { active: !t.active }), inv);
  const convert = useAct((id: string) => api.post(`/config/modelos/${id}/converter`), inv, () => { const id = legacy!.id; setLegacy(null); setEditorId(id); }, "Modelo convertido para Word");
  const open = (t: TemplateRow) => (t.kind === "docx" ? setEditorId(t.id) : setLegacy(t));
  return (
    <Card title="Modelos de documento" actions={<button className="btn small primary" onClick={() => setNewWord(true)}>Novo modelo (Word)</button>}>
      <div className="muted small" style={{ marginBottom: 8 }}>Envie o Word que o escritório já usa e marque, na tela, onde entram os dados do sistema e as regras.</div>
      <ErrorBox error={error ?? dup.error ?? toggle.error} />
      {!data ? <Loading /> : data.map((t) => (
        <div key={t.id} className="lrow" onClick={() => open(t)}>
          <div className="grow">
            <div className="tt">{t.name} {!t.active && <Badge tone="muted">desativado</Badge>} {t.kind === "texto" && <Badge tone="warn">formato antigo</Badge>}</div>
            <div className="mm">{t.description ?? ""} · a partir de {CONTEXT_LABEL[t.context].toLowerCase()} · v{t.version}</div>
          </div>
          <button className="btn small" onClick={(e) => { e.stopPropagation(); open(t); }}>Editar</button>
          <button className="btn small" onClick={(e) => { e.stopPropagation(); dup.mutate(t.id); }}>Duplicar</button>
          <button className="btn small ghost" onClick={(e) => { e.stopPropagation(); toggle.mutate(t); }}>{t.active ? "Desativar" : "Ativar"}</button>
        </div>
      ))}
      <div className="muted small" style={{ marginTop: 8 }}><button className="link-btn" onClick={() => setEdit("new")}>Criar modelo de texto (modo avançado)</button></div>
      {edit && <TemplateModal tpl={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
      {newWord && <NewWordTemplate onClose={() => setNewWord(false)} onCreated={(id) => { setNewWord(false); setEditorId(id); }} />}
      {editorId && <TemplateEditor id={editorId} onClose={() => setEditorId(null)} />}
      {legacy && (
        <Modal title={legacy.name} onClose={() => setLegacy(null)}
          footer={<>
            <button className="btn" style={{ marginRight: "auto" }} onClick={() => { const t = legacy; setLegacy(null); setEdit(t); }}>Modo avançado (texto)</button>
            <button className="btn primary" disabled={convert.isPending} onClick={() => convert.mutate(legacy.id)}>{convert.isPending ? "Convertendo…" : "Converter e abrir no editor"}</button>
          </>}>
          <div className="stack gap-8 small">
            <div>Este modelo está no <b>formato antigo</b> (texto com códigos). Para editar do jeito novo, ele é convertido em Word, com os mesmos campos e regras já marcados.</div>
            <div>O documento gerado continua igual (mesmo layout de PDF e timbrado). A versão antiga fica guardada no histórico e pode ser restaurada.</div>
          </div>
          <ErrorBox error={convert.error} />
        </Modal>
      )}
    </Card>
  );
}

const HELP = `# TÍTULO  ·  ## SEÇÃO  ·  **negrito**
^ centralizado  ·  < à esquerda  ·  > à direita  ·  ~ recuo de parágrafo
@assinatura Nome | linha 2  ·  @quebra (nova página)
{{campo}}  ·  {{campo|maiusculas}}  ·  {{#se campo}}…{{/se}}`;

function TemplateModal({ tpl, onClose }: { tpl: TemplateRow | null; onClose: () => void }): JSX.Element {
  const fields = useQuery({ queryKey: ["modelos-campos"], queryFn: () => api.get<{ key: string; label: string }[]>("/modelos-documento/campos") });
  const [f, setF] = useState({
    name: tpl?.name ?? "", description: tpl?.description ?? "", context: tpl?.context ?? "pessoa",
    body: tpl?.body ?? "# TÍTULO\n\n{{cliente.nome_qualificado}}, …\n\n< {{data.local}}.\n\n@assinatura {{cliente.nome}}",
    inputs: tpl?.inputs ?? [], required: tpl?.required ?? [], letterhead: tpl?.letterhead ?? true, letterheadId: tpl?.letterheadId ?? null,
  });
  const lhs = useQuery({ queryKey: ["timbrados"], queryFn: () => api.get<Array<{ id: string; name: string; isDefault: boolean }>>("/timbrados") });
  const lhValue = !f.letterhead ? "none" : f.letterheadId ?? "";
  const area = useRef<HTMLTextAreaElement>(null);
  const inv = [["config-modelos"], ["modelos-documento"]];
  const save = useAct(() => (tpl ? api.patch(`/config/modelos/${tpl.id}`, f) : api.post("/config/modelos", f)), inv, onClose);
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useAct(async (file: File) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.upload<{ placeholders: string[]; createdInputs: string[] }>(`/config/modelos/${tpl!.id}/arquivo`, fd);
    toast.ok(`${r.placeholders.length} campos encontrados${r.createdInputs.length ? `; ${r.createdInputs.length} viraram campos a preencher` : ""}`);
    return r;
  }, inv, onClose, false);
  const dropFile = useAct(() => api.del(`/config/modelos/${tpl!.id}/arquivo`), inv, onClose, "Modelo voltou a ser texto");
  const [testErr, setTestErr] = useState<unknown>(null);
  const [showPreview, setShowPreview] = useState(false);

  const insert = (text: string) => {
    const el = area.current;
    if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;
    const body = f.body.slice(0, s) + text + f.body.slice(e);
    setF({ ...f, body });
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + text.length; });
  };
  const setInput = (i: number, patch: Partial<InputDef>) => setF({ ...f, inputs: f.inputs.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const toggleReq = (k: string) => setF({ ...f, required: f.required.includes(k) ? f.required.filter((x) => x !== k) : [...f.required, k] });
  const isDocx = tpl?.kind === "docx";

  return (
    <Modal wide title={tpl ? `Modelo — ${tpl.name}` : "Novo modelo"} onClose={onClose} footer={
      <>
        {!isDocx && <button className="btn" style={{ marginRight: "auto" }} onClick={() => { setTestErr(null); api.openPost("/config/modelos/teste", { body: f.body, inputs: f.inputs, letterhead: f.letterhead, letterheadId: f.letterheadId }).catch(setTestErr); }}>Testar em PDF</button>}
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={save.isPending || f.name.trim().length < 2} onClick={() => save.mutate()}>Salvar</button>
      </>
    }>
      <ErrorBox error={save.error ?? upload.error ?? dropFile.error ?? testErr} />
      <FormGrid cols={3}>
        <Field label="Nome" required><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Gerado a partir de">
          <select className="input" value={f.context} onChange={(e) => setF({ ...f, context: e.target.value as TemplateRow["context"] })}>
            <option value="pessoa">Pessoa (ou caso/processo)</option><option value="caso">Caso (ou processo)</option><option value="processo">Processo</option><option value="financeiro">Financeiro (recibo)</option>
          </select>
        </Field>
        <Field label="Timbrado">
          <select className="input" value={lhValue} onChange={(e) => {
            const v = e.target.value;
            setF({ ...f, letterhead: v !== "none", letterheadId: v && v !== "none" ? v : null });
          }}>
            <option value="">Automático (unidade → padrão)</option>
            {(lhs.data ?? []).map((l) => <option key={l.id} value={l.id}>Sempre: {l.name}</option>)}
            <option value="none">Sem timbrado</option>
          </select>
        </Field>
        <Field label="Descrição" span={3}><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      </FormGrid>

      {tpl && (
        <div className="row" style={{ margin: "10px 0" }}>
          <input ref={fileRef} type="file" hidden accept=".docx" onChange={(e) => { const x = e.target.files?.[0]; if (x) upload.mutate(x); e.target.value = ""; }} />
          {isDocx ? (
            <>
              <Badge tone="info">Arquivo Word: {tpl.fileName}</Badge>
              <button className="btn small" onClick={() => void api.download(`/config/modelos/${tpl.id}/arquivo`, tpl.fileName ?? "modelo.docx")}>Baixar</button>
              <button className="btn small" onClick={() => fileRef.current?.click()}>Trocar arquivo</button>
              <button className="btn small ghost" onClick={() => dropFile.mutate()}>Voltar para texto</button>
            </>
          ) : (
            <>
              <button className="btn small" onClick={() => fileRef.current?.click()}>Usar arquivo Word (.docx)…</button>
              <span className="muted small">Escreva {"{{cliente.nome}}"}, {"{{NOME}}"}, {"{{QUALIFICACAO}}"}… no Word; campos desconhecidos viram perguntas na hora de gerar.</span>
            </>
          )}
        </div>
      )}

      {!isDocx && (
        <div className="gen-layout" style={{ gridTemplateColumns: "1fr 260px" }}>
          <div className="stack gap-8">
            <div className="row">
              <span className="muted small grow" style={{ whiteSpace: "pre-line" }}>{HELP}</span>
              <button className="btn small ghost" onClick={() => setShowPreview(!showPreview)}>{showPreview ? "Editar" : "Ver estrutura"}</button>
            </div>
            {showPreview ? <PreviewStructure body={f.body} /> : (
              <textarea ref={area} className="input code-area" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} spellCheck={false} />
            )}
          </div>
          <div className="stack gap-8">
            <div className="fld-label">Campos (clique para inserir)</div>
            <div className="field-list">
              {f.inputs.map((i) => (
                <button key={`c-${i.key}`} onClick={() => insert(`{{campo.${i.key}}}`)} title={i.label}><code>campo.{i.key}</code><div className="muted">{i.label}</div></button>
              ))}
              {(fields.data ?? []).map((x) => (
                <button key={x.key} onClick={() => insert(`{{${x.key}}}`)} title={x.label}><code>{x.key}</code><div className="muted">{x.label}</div></button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="fld-label" style={{ marginTop: 14 }}>Perguntas na hora de gerar</div>
      <div className="stack gap-4">
        {f.inputs.map((i, n) => (
          <div key={n} className="row nowrap">
            <input className="input" style={{ width: 130 }} placeholder="chave" value={i.key} onChange={(e) => setInput(n, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} />
            <input className="input" placeholder="Pergunta (ex.: Valor total)" value={i.label} onChange={(e) => setInput(n, { label: e.target.value })} />
            <select className="input" style={{ width: 130 }} value={i.type} onChange={(e) => setInput(n, { type: e.target.value as InputDef["type"] })}>
              <option value="texto">Texto</option><option value="textolongo">Texto longo</option><option value="dinheiro">Dinheiro</option><option value="data">Data</option><option value="numero">Número</option>
            </select>
            <input className="input" style={{ width: 170 }} placeholder="Padrão ({{caso.area}})" value={i.default ?? ""} onChange={(e) => setInput(n, { default: e.target.value })} />
            <label className="row nowrap small"><input type="checkbox" checked={i.required} onChange={(e) => setInput(n, { required: e.target.checked })} /> obrigatória</label>
            <button className="btn ghost small" onClick={() => setF({ ...f, inputs: f.inputs.filter((_, j) => j !== n) })}>✕</button>
          </div>
        ))}
        <div><button className="btn small" onClick={() => setF({ ...f, inputs: [...f.inputs, { key: `campo${f.inputs.length + 1}`, label: "", type: "texto", required: false, default: "" }] })}>+ Pergunta</button>
          <span className="muted small"> Dinheiro e data também criam <code>campo.chave_extenso</code>.</span></div>
      </div>

      <div className="fld-label" style={{ marginTop: 14 }}>Dados obrigatórios do cadastro</div>
      <div className="chips">
        {(fields.data ?? []).filter((x) => x.key.startsWith("cliente.") || x.key.startsWith("processo.")).map((x) => (
          <button key={x.key} type="button" className={`chip ${f.required.includes(x.key) ? "on" : ""}`} onClick={() => toggleReq(x.key)}>{x.key.replace(/^cliente\./, "").replace(/_/g, " ")}</button>
        ))}
      </div>
    </Modal>
  );
}

/** Estrutura do texto sem dados (campos aparecem como [campo]). */
function PreviewStructure({ body }: { body: string }): JSX.Element {
  const text = body.replace(/\{\{#(se|senao)\s+[\w.]+\s*\}\}|\{\{\/(se|senao)\}\}/g, "").replace(/\{\{\s*([\w.]+)[^}]*\}\}/g, "[$1]");
  const blocks = text.split("\n").map((raw) => {
    const line = raw.trimEnd();
    const runs = (s: string, bold = false) => s.split("**").filter((p, i) => p || i).map((p, i) => ({ text: p, bold: bold || i % 2 === 1 }));
    if (!line.trim()) return { t: "blank" as const };
    if (line.startsWith("@quebra")) return { t: "break" as const };
    if (line.startsWith("@assinatura")) return { t: "sign" as const, lines: line.slice(11).split("|").map((x, i) => runs(x.trim(), i === 0)) };
    if (line.startsWith("## ")) return { t: "section" as const, runs: runs(line.slice(3), true) };
    if (line.startsWith("# ")) return { t: "title" as const, runs: runs(line.slice(2), true) };
    const m = line.match(/^([\^<>~])\s?(.*)$/);
    const ALIGN: Record<string, "center" | "left" | "right" | "justify"> = { "^": "center", "<": "left", ">": "right", "~": "justify" };
    const align = m ? ALIGN[m[1]!]! : "justify";
    return { t: "p" as const, align, indent: m?.[1] === "~", runs: runs(m ? m[2]! : line) };
  });
  return <Paper blocks={blocks} />;
}
